<script>
export const pageName = 'tps';
export const pageRoute = '/tps';
export const title = 'Team Project Support'
export const requireMembership = true;
export const requireCompleteAccount = true;

import { commands } from '../../js/lib/functions.js';
import { navigate } from '@trullock/page-manager'
import { showToast } from '../../js/utils/toast.js'
import { hideLoading, showLoading } from '../../js/utils/loading.js';
import { listenToCurrentUserView } from '../../js/readModel.js';
import { cropImage } from '../../js/utils/cropper.js';

export default {
	data() {
		return {
			currentUser: null,

			teamName: null,
			teamLogo: null,
			email: null,
			institution: null,
			project: null,
			members: [],

			ready: false
		}
	},

	methods: {
		async boot(opts) {
			await listenToCurrentUserView(async user => {
				this.currentUser = user;
			});	
			this.ready = true;
		},

		show(opts) {
			this.members = [
				{
					name: this.currentUser.name,
					ukraNumber: this.currentUser.number,
					experience: ""
				}
			]
		},

		async apply() {
			
			showLoading();

			let result = await commands.users.tps.apply({
				teamName: this.teamName,
				teamLogo: this.teamLogo,
				email: this.email,
				institution: this.institution,
				project: this.project,
				members: this.members
			});
			
			if(!result.success)
			{
				showToast({ message: 'Error submitting application', style: 'bg-danger'})
				hideLoading();
				return;
			}

			this.teamName = null;
			this.teamLogo = null;
			this.email = null;
			this.institution = null;
			this.project = null;
			this.members = [];

			navigate('/');

			showToast({
				message: 'Application submitted',
				style: 'bg-success'
			})

			hideLoading();
		},

		addMember() {
			this.members.push({
				name: "",
				ukraNumber: "",
				experience: ""
			})
		},

		deleteMember(member) {
			let index = this.members.findIndex(m => m == member);
			if(index > 0) // prevent self deletion
				this.members.splice(index, 1);
		},

		teamLogoChanged(e) {
			var files = e.target.files;

			if (files && files.length > 0) {
				let file = files[0];

				this.$refs.fupTeamLogo.value = '';
				this.$refs.fupTeamLogo.blobData = null;
				
				cropImage({
					aspectRatio: 1,
					viewMode: 1,
					autoCropArea: 1
				}, { 
					width: 240, 
					height: 240 
				}, file).then(canvas => {
					this.teamLogo = canvas.toDataURL();

					canvas.toBlob(blob => {
						this.$refs.fupTeamLogo.blobData = {
							blob: blob,
							name: 'avatar.jpg'
						};
						
						this.$refs.lblTeamLogo.innerText = file.name;
					});
				}, e => {
					if(e instanceof Error)
					{
						this.$refs.fupFlightCard.setCustomValidity(e.message);
						this.$refs.fupFlightCard.reportValidity();
					}
				})
			}
		}
	}
}

</script>
<template>
	<div v-if="ready" class="container py-5">
		<div class="row">
			<div class="col col-12">
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Team Project Support</h4>
					</div>
					<div class="card-body">
						<p>The TPS scheme assists teams from universities and other recognised bodies to develop, build and launch a rocket safely without the requirements normally imposed by the UKRA certification process.</p>
						<p class="mb-0">Detailed information about the <a href="https://ukra.org.uk/membership/team-project-support/" target="_blank">Team Project Support programme <span class="fe fe-external-link"></span></a> can be found on the main UKRA website.</p>
					</div>
				</div>
			</div>
		</div>
		<div class="row">
			<div class="col col-12 col-lg-8">
				<div class="card" v-if="currentUser.tps.status == 'accepted'">
					<div class="card-header">
						<h4 class="card-header-title">Accepted</h4>
					</div>
					<div class="card-body">
						<p>Your TPS application has been accepted.</p>
					</div>
				</div>
				<div class="card" v-if="currentUser.tps.status == 'pending'">
					<div class="card-header">
						<h4 class="card-header-title">Pending</h4>
					</div>
					<div class="card-body">
						<p>Your TPS application is pending.</p>
						<p class="alert alert-light small">UKRA is staffed by volunteers on a part time basis and will respond to your enquiry as soon as possible.</p>
					</div>
				</div>
				<div class="card" v-if="currentUser.tps.status == 'none'">
					<div class="card-header">
						<h4 class="card-header-title">Application</h4>
					</div>
					<div class="card-body">
						<form ref="form" @submit.prevent="apply">
							<div class="form-group">
								<label class="form-label mb-1">Team name</label>
								<input type="text" class="form-control" v-model="teamName" required />
								<span class="invalid-feedback"></span>
							</div>
							<div class="field form-group">
								<label class="d-block">
									Team logo
									
									<div class="row justify-content-center my-3" v-if="teamLogo">
										<div class="col-auto">
											<div class="dz-default dz-message py-4">
												<div class="avatar avatar-xxl">
													<img ref="imgTeamLogo" class="avatar-img rounded-circle" :src="teamLogo" alt="Team Logo">
												</div>
											</div>
										</div>
									</div>
								</label>
								<div class="custom-file">
									<input type="file" @change="teamLogoChanged" ref="fupTeamLogo" class="custom-file-input" accept="image/jpg;image/png">
									<label ref="lblTeamLogo" class="custom-file-label text-muted">Choose image&hellip;</label>
									<span class="invalid-feedback"></span>
								</div>
								<span class="form-text text-muted small mb-0">Optional</span>
							</div>
							<div class="form-group">
								<label class="form-label mb-1">Email</label>
								<input type="email" class="form-control" v-model="email" required />
								<span class="invalid-feedback"></span>
								<p class="form-text text-muted small mb-0">The primary contact email for your team.</p>
							</div>
							<div class="form-group">
								<label class="form-label mb-1">Institution</label>
								<input type="text" class="form-control" placeholder="e.g. University of Rocketry" v-model="institution" required />
								<span class="invalid-feedback"></span>
							</div>
							<div class="form-group">
								<label class="form-label mb-1">Your project</label>
								<textarea class="form-control" v-model="project" required rows="4"></textarea>
								<span class="invalid-feedback"></span>
								<p class="form-text text-muted small mb-0">Supply a detailed description of what you're wanting to achieve.</p>
							</div>

							<hr class="my-5" />
							<h4>Members</h4>
							<p class="form-text text-muted small">Provide details of all team members.</p>

							<div v-for="(member, i) of members" class="row align-items-center">
								<div class="col">
									<div class="form-group">
										<label class="form-label mb-1">Name</label>
										<input type="text" class="form-control" :readonly="i == 0" v-model="member.name" required />
										<span class="invalid-feedback"></span>
									</div>
								</div>
								<div class="col">
									<div class="form-group">
										<label class="form-label mb-1">UKRA Number</label>
										<input type="text" class="form-control" :readonly="i == 0" v-model="member.ukraNumber" />
										<span class="invalid-feedback"></span>
									</div>
								</div>
								<div class="col">
									<div class="form-group">
										<label class="form-label mb-1">Rocketry experience</label>
										<input type="text" class="form-control" v-model="member.experience" required />
										<span class="invalid-feedback"></span>
									</div>
								</div>
								<div class="col-auto">
									<button :disabled="i == 0" @click.prevent="deleteMember(member)" :class="`${i == 0 ? 'text-secondary' : 'text-danger'}`"><span class="fe fe-trash-2"></span></button>
								</div>
							</div>
							<button @click.prevent="addMember" class="btn btn-outline-primary btn-block">Add team member <span class="fe fe-plus-circle"></span></button>
							
							<hr class="my-5" />
							<button type="submit" class="btn btn-lg btn-primary btn-block">Send Application <span class="fe fe-check-circle"></span></button>
						</form>
					</div>
				</div>
			</div>
			<div class="col col-12 col-lg-4">
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Progress</h4>
					</div>
					<div class="card-body">
						<div class="text-secondary small mb-4">Complete an application and interview.</div>
						<div class="list-group list-group-flush my-n3">
							<div class="list-group-item">
								<div class="row">
									<div class="col small">
										Application
									</div>
									<div class="col-auto">
										<div :class="`badge ${currentUser.tps.status != 'none' ? 'badge-success' : 'badge-light'}`">
											{{ currentUser.tps.status != 'none' ? 'Passed' : 'Not sent' }}
										</div>
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row">
									<div class="col small">
										Interview
									</div>
									<div class="col-auto">
										<div :class="`badge ${currentUser.tps.status == 'approved'  ? 'badge-success' : 'badge-light'}`">
											{{ currentUser.tps.status == 'approved' ? 'Passed' : 'Not passed' }}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
    </div>
</template>
